import { useState } from "react";
import { UsersThree, UserList, Folder, CurrencyDollar } from "@phosphor-icons/react";
import { fmtCurrency } from "@/lib/format";
import { useAdminData } from "@/hooks/useAdminData";

const TABS = [
  { id: "advisors", label: "Advisors" },
  { id: "leads", label: "All leads" },
  { id: "scenarios", label: "All scenarios" },
];

const ADVISORS_HEAD = ["Advisor", "Ref code", "Rate", "Leads (total)", "Leads (cycle)", "Cycle charge", "Scenarios", "Joined"];
const LEADS_HEAD = ["Lead", "Business", "Industry", "Advisor", "10-yr teaser", "Source", "Date"];
const SCENARIOS_HEAD = ["Client", "Business", "Advisor", "Wealth gain", "Horizon", "Date"];

const fmtDate = (v) =>
  v ? new Date(v).toLocaleDateString("en-CA", { year: "numeric", month: "short", day: "numeric" }) : "—";

export default function Admin() {
  const { overview, leads, scenarios, loading } = useAdminData();
  const [tab, setTab] = useState("advisors");

  if (loading) {
    return (
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-16">
        <div className="bg-white border border-brand-line rounded-md p-8 h-96 animate-pulse" />
      </main>
    );
  }

  return (
    <main data-testid="admin-page" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-16">
      <PageIntro />
      <StatGrid overview={overview} />
      <TabBar tab={tab} setTab={setTab} />
      {tab === "advisors" && <AdvisorsTable rows={overview?.advisors ?? []} />}
      {tab === "leads" && <LeadsTable rows={leads} />}
      {tab === "scenarios" && <ScenariosTable rows={scenarios} />}
    </main>
  );
}

function PageIntro() {
  return (
    <div className="mb-10">
      <span className="label-eyebrow">Platform owner</span>
      <h1 className="mt-3 font-serif text-4xl md:text-5xl text-brand-forest leading-tight">
        Admin overview
      </h1>
      <p className="mt-3 text-brand-ink/70 max-w-2xl">
        Full visibility across every advisor account — leads, scenarios, and per-lead
        billing for the current cycle. Advisors never see this view.
      </p>
    </div>
  );
}

function StatGrid({ overview }) {
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mb-10">
      <Stat icon={UsersThree} label="Advisors" value={overview?.total_advisors ?? 0} testId="admin-stat-advisors" />
      <Stat icon={UserList} label="Total leads" value={overview?.total_leads ?? 0} testId="admin-stat-leads" />
      <Stat icon={Folder} label="Total scenarios" value={overview?.total_scenarios ?? 0} testId="admin-stat-scenarios" />
      <Stat
        icon={CurrencyDollar}
        label="Cycle billings"
        value={fmtCurrency(overview?.cycle_revenue ?? 0)}
        testId="admin-stat-revenue"
        accent
      />
    </div>
  );
}

function TabBar({ tab, setTab }) {
  return (
    <div className="flex gap-2 mb-6">
      {TABS.map((t) => (
        <button
          key={t.id}
          onClick={() => setTab(t.id)}
          data-testid={`admin-tab-${t.id}`}
          className={`px-4 py-2 rounded-md text-sm font-semibold transition-colors ${
            tab === t.id
              ? "bg-brand-forest text-brand-bone"
              : "bg-white border border-brand-line text-brand-ink/70 hover:text-brand-forest"
          }`}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

function Stat({ icon: Icon, label, value, testId, accent }) {
  return (
    <div data-testid={testId} className="bg-white border border-brand-line rounded-md p-5">
      <div className="flex items-center gap-2 text-brand-muted">
        <Icon size={15} weight="duotone" />
        <span className="text-[10px] uppercase tracking-[0.16em]">{label}</span>
      </div>
      <div className={`mt-2 font-serif text-3xl ${accent ? "text-brand-copper" : "text-brand-forest"}`}>
        {value}
      </div>
    </div>
  );
}

function Table({ head, children, testId }) {
  return (
    <div data-testid={testId} className="bg-white border border-brand-line rounded-md overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-brand-line">
            {head.map((h) => (
              <th key={h} className="text-left px-4 py-3 text-[10px] uppercase tracking-[0.16em] text-brand-muted font-semibold whitespace-nowrap">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

function AdvisorsTable({ rows }) {
  return (
    <Table testId="admin-advisors-table" head={ADVISORS_HEAD}>
      {rows.map((a) => (
        <tr key={a.user_id} data-testid={`admin-advisor-row-${a.user_id}`} className="border-b border-brand-line last:border-0">
          <td className="px-4 py-3">
            <div className="font-semibold text-brand-forest">
              {a.name || a.email}
              {a.role === "admin" && (
                <span className="ml-2 text-[10px] uppercase tracking-wider bg-brand-copper/15 text-brand-copper px-1.5 py-0.5 rounded">
                  Owner
                </span>
              )}
            </div>
            <div className="text-xs text-brand-muted">{a.email}</div>
          </td>
          <td className="px-4 py-3 font-mono text-xs">{a.ref_code}</td>
          <td className="px-4 py-3">{fmtCurrency(a.per_lead_rate)}/lead</td>
          <td className="px-4 py-3">{a.total_leads}</td>
          <td className="px-4 py-3">{a.cycle_leads}</td>
          <td className="px-4 py-3 font-semibold text-brand-copper">{fmtCurrency(a.cycle_charge)}</td>
          <td className="px-4 py-3">{a.total_scenarios}</td>
          <td className="px-4 py-3 text-xs text-brand-muted">{fmtDate(a.created_at)}</td>
        </tr>
      ))}
    </Table>
  );
}

function LeadsTable({ rows }) {
  if (rows.length === 0) {
    return <Empty text="No leads on the platform yet." testId="admin-leads-empty" />;
  }
  return (
    <Table testId="admin-leads-table" head={LEADS_HEAD}>
      {rows.map((l) => (
        <tr key={l.id} className="border-b border-brand-line last:border-0">
          <td className="px-4 py-3">
            <div className="font-semibold text-brand-forest">{l.full_name}</div>
            <div className="text-xs text-brand-muted">{l.email}</div>
          </td>
          <td className="px-4 py-3">{l.business_name || "—"}</td>
          <td className="px-4 py-3 text-xs">{l.industry || "—"}</td>
          <td className="px-4 py-3 text-xs">{l.advisor_name || l.advisor_email || "Unassigned"}</td>
          <td className="px-4 py-3 font-semibold text-brand-copper">{fmtCurrency(l.teaser_wealth_gain)}</td>
          <td className="px-4 py-3 font-mono text-xs">{l.ref || "—"}</td>
          <td className="px-4 py-3 text-xs text-brand-muted">{fmtDate(l.created_at)}</td>
        </tr>
      ))}
    </Table>
  );
}

function ScenariosTable({ rows }) {
  if (rows.length === 0) {
    return <Empty text="No scenarios on the platform yet." testId="admin-scenarios-empty" />;
  }
  return (
    <Table testId="admin-scenarios-table" head={SCENARIOS_HEAD}>
      {rows.map((s) => (
        <tr key={s.id} className="border-b border-brand-line last:border-0">
          <td className="px-4 py-3 font-semibold text-brand-forest">{s.client_name}</td>
          <td className="px-4 py-3">{s.business_name || "—"}</td>
          <td className="px-4 py-3 text-xs">{s.advisor_name || s.advisor_email || "Unassigned"}</td>
          <td className="px-4 py-3 font-semibold text-brand-copper">{fmtCurrency(s.headline_wealth_gain)}</td>
          <td className="px-4 py-3">{s.horizon_years} yrs</td>
          <td className="px-4 py-3 text-xs text-brand-muted">{fmtDate(s.created_at)}</td>
        </tr>
      ))}
    </Table>
  );
}

function Empty({ text, testId }) {
  return (
    <div data-testid={testId} className="bg-white border border-brand-line border-dashed rounded-md p-10 text-center text-sm text-brand-muted">
      {text}
    </div>
  );
}
